import { HealthProfile, HealthRecord } from '../healthTypes';
import {
  getAllHealthProfilesFromDB,
  saveHealthProfilesToDB,
  getAllHealthRecordsFromDB,
  saveHealthRecordsToDB
} from './db';

const KEYS = {
  PROFILES: 'health_profiles',
  RECORDS: 'health_records',
  ACTIVE_PROFILE: 'health_active_profile_id',
};

// Migration helper: Move data from localStorage to IndexedDB
const migrateFromLocalStorage = async () => {
  try {
    // Check if we have data in localStorage
    const profilesData = localStorage.getItem(KEYS.PROFILES);
    const recordsData = localStorage.getItem(KEYS.RECORDS);

    if (profilesData || recordsData) {
      const profiles: HealthProfile[] = profilesData ? JSON.parse(profilesData) : [];
      const records: HealthRecord[] = recordsData ? JSON.parse(recordsData) : [];

      // Save to IndexedDB
      if (profiles.length > 0) {
        await saveHealthProfilesToDB(profiles);
      }
      if (records.length > 0) {
        await saveHealthRecordsToDB(records);
      }

      // Clear localStorage after successful migration
      localStorage.removeItem(KEYS.PROFILES);
      localStorage.removeItem(KEYS.RECORDS);

      console.log('Health data migrated from localStorage to IndexedDB');
    }
  } catch (error) {
    console.error('Failed to migrate health data:', error);
  }
};

export const healthStorageService = {
  getProfiles: async (): Promise<HealthProfile[]> => {
    try {
      // Try migration first
      await migrateFromLocalStorage();

      const profiles = await getAllHealthProfilesFromDB();
      return profiles;
    } catch (error) {
      console.error('Failed to get profiles:', error);
      return [];
    }
  },

  saveProfile: async (profile: HealthProfile): Promise<void> => {
    try {
      const profiles = await healthStorageService.getProfiles();
      const existingIndex = profiles.findIndex(p => p.id === profile.id);

      if (existingIndex >= 0) {
        profiles[existingIndex] = { ...profile, updatedAt: new Date().toISOString() };
      } else {
        profiles.push({ ...profile, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() });
      }

      await saveHealthProfilesToDB(profiles);
    } catch (error) {
      console.error('Failed to save profile:', error);
    }
  },

  deleteProfile: async (id: string): Promise<void> => {
    try {
      const profiles = (await healthStorageService.getProfiles()).filter(p => p.id !== id);
      await saveHealthProfilesToDB(profiles);

      const records = (await healthStorageService.getRecords()).filter(r => r.profileId !== id);
      await saveHealthRecordsToDB(records);
    } catch (error) {
      console.error('Failed to delete profile:', error);
    }
  },

  getRecords: async (profileId?: string): Promise<HealthRecord[]> => {
    try {
      // Try migration first
      await migrateFromLocalStorage();

      let records = await getAllHealthRecordsFromDB();

      if (profileId) {
        records = records.filter(r => r.profileId === profileId);
      }

      return records.sort((a, b) => b.timestamp - a.timestamp);
    } catch (error) {
      console.error('Failed to get records:', error);
      return [];
    }
  },

  saveRecord: async (record: HealthRecord): Promise<void> => {
    try {
      const records = await healthStorageService.getRecords();
      const existingIndex = records.findIndex(r => r.id === record.id);

      if (existingIndex >= 0) {
        records[existingIndex] = { ...record, updatedAt: new Date().toISOString() };
      } else {
        records.push({ ...record, updatedAt: new Date().toISOString() });
      }

      await saveHealthRecordsToDB(records);
    } catch (error) {
      console.error('Failed to save record:', error);
    }
  },

  deleteRecord: async (id: string): Promise<void> => {
    try {
      const records = (await healthStorageService.getRecords()).filter(r => r.id !== id);
      await saveHealthRecordsToDB(records);
    } catch (error) {
      console.error('Failed to delete record:', error);
    }
  },

  getActiveProfileId: (): string | null => {
    return localStorage.getItem(KEYS.ACTIVE_PROFILE);
  },

  setActiveProfileId: (id: string): void => {
    localStorage.setItem(KEYS.ACTIVE_PROFILE, id);
  }
};
